import { NgZone } from "@angular/core";
import { Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpResponse, HttpHandler, HttpEvent, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Observable, throwError, BehaviorSubject, from } from "rxjs";
import { AlertController, NavController } from "@ionic/angular";
import { Router } from "@angular/router";
import { map, catchError, filter, take, switchMap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { SecureStorageService } from "../services/secure-storage.service";


@Injectable()
export class Intercept implements HttpInterceptor {
  
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private lastRefreshCheck = 0;

  constructor(
    private router: Router,
    private navCtrl: NavController,
    public alertController: AlertController,
    private secureStorage: SecureStorageService
  ) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    let userData: any = {};
    if (localStorage.getItem("user")) {
      let tempData: any = localStorage.getItem("user");
      userData = JSON.parse(tempData);
    }

    // Removed console logging for production

    // Check if we need to refresh the token before making the request
    // Add a small delay to prevent multiple simultaneous checks
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastRefreshCheck;
    
    if (userData && this.shouldRefreshToken(userData)) {
      // Don't refresh for token refresh requests to avoid infinite loops
      if (!request.url.includes('/token/refresh')) {
        // Only initiate refresh if we haven't checked very recently (within 500ms)
        // This prevents multiple simultaneous requests from all trying to refresh
        if (!this.isRefreshing && timeSinceLastCheck > 500) {
          this.lastRefreshCheck = now;
          return this.handleTokenRefresh(request, next, userData);
        } else if (this.isRefreshing) {
          // If refresh is already in progress, queue this request
          return this.handleTokenRefresh(request, next, userData);
        }
      }
    }

    // Check for 'accessToken' field which is what the backend provides
    if (userData && userData.accessToken) {
      request = request.clone({
        setHeaders: { Authorization: `Bearer ${userData.accessToken}` }
      });
    } else {
      // Silent - no token is normal for public endpoints
      // Don't show alert for missing token, just redirect quietly
      // The actual 401 response will be handled below
      if (this.router.url != "/login" && 
          this.router.url != "/signup" && 
          this.router.url != "/forgot-password" && 
          !this.router.url.includes('reset-password')) {
        // Store the intended URL for redirecting after login
        const returnUrl = this.router.url;
        if (returnUrl && returnUrl !== '/') {
          localStorage.setItem('returnUrl', returnUrl);
        }
      }
    }

    // return next.handle(request);

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Handle standardized success responses
          const body = event.body;

          if (body && body.success === true) {
            // New standardized success format - extract data for backward compatibility
            const extractedData = body.data !== undefined ? body.data : body;

            if (!environment.production && body.requestId) {
              console.log(`✅ API Success [${event.status}] ${body.message || 'Success'}`, {
                requestId: body.requestId,
                endpoint: request.url.split('/api/')[1]?.split('?')[0] || 'unknown'
              });
            }

            // Return response with extracted data to maintain backward compatibility
            return event.clone({ body: extractedData });
          }

          // Legacy response format - pass through unchanged
          if (!environment.production) {
            const endpoint = request.url.split('/api/')[1]?.split('?')[0] || 'unknown';
            console.log(`✅ Legacy Success [${event.status}] on ${endpoint}`);
          }
        }

        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        let data = { ...error };

        // Extract standardized error response if available
        const errorResponse = error.error;
        let errorMessage = 'An error occurred';
        let errorCode = 'UNKNOWN_ERROR';
        let requestId = null;

        if (errorResponse && errorResponse.success === false) {
          // New standardized error format
          errorMessage = errorResponse.error?.message || errorMessage;
          errorCode = errorResponse.error?.code || errorCode;
          requestId = errorResponse.requestId;

          if (!environment.production) {
            console.error(`🚫 API Error [${error.status}] ${errorCode}: ${errorMessage}`, {
              requestId,
              endpoint: request.url.split('/api/')[1]?.split('?')[0] || 'unknown',
              details: errorResponse.error?.details
            });
          }
        } else if (error.error && typeof error.error === 'object') {
          // Legacy error formats - for backward compatibility during transition
          errorMessage = error.error.message || error.error.error || errorMessage;

          if (!environment.production) {
            const endpoint = request.url.split('/api/')[1]?.split('?')[0] || 'unknown';
            console.error(`🚫 Legacy Error [${error.status}] on ${endpoint}:`, error.error);
          }
        } else {
          // Network or other errors
          errorMessage = error.message || 'Network error occurred';

          if (!environment.production) {
            console.error(`🚫 Network Error [${error.status}]:`, error);
          }
        }

        if (
          data.status == 401 &&
          this.router.url != "/login" &&
          this.router.url != "/signup" &&
          this.router.url != "/forgot-password" &&
          !this.router.url.includes('reset-password')
        ) {
          // Check if we just logged in (within last 5 seconds) to avoid race conditions
          const loginTime = localStorage.getItem('loginTime');
          const now = Date.now();
          if (loginTime && (now - parseInt(loginTime)) < 5000) {
            console.log('⏱️ Ignoring 401 - just logged in');
            return throwError(error);
          }

          // Try to refresh token if we have a refresh token and not already refreshing
          const storedUserData = localStorage.getItem("user");
          if (storedUserData) {
            const userData = JSON.parse(storedUserData);
            if (userData && userData.refreshToken && !this.isRefreshing && !request.url.includes('/token/refresh')) {
              console.log('🔄 401 - attempting refresh');
              return this.handleTokenRefresh(request, next, userData);
            }
          }
          
          // Store the intended URL for redirecting after login
          const returnUrl = this.router.url;
          if (returnUrl && returnUrl !== '/') {
            localStorage.setItem('returnUrl', returnUrl);
          }
          
          // Clear auth data
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("loginTime");
          localStorage.removeItem("refreshToken");
          
          // Navigate to login without showing alert
          this.navCtrl.navigateRoot("/login");
        }
        return throwError(error);
      })
    );

  }

  async alertPopup(title: string, msg: string) {
    const alert = await this.alertController.create({
      header: title,
      message: msg,
      buttons: [{
        text: 'Okay'
      }]
    });
    await alert.present();
  }

  private shouldRefreshToken(userData: any): boolean {
    if (!userData || !userData.loginTime || !userData.expiresIn) {
      return false;
    }

    const loginTime = userData.loginTime;
    const expiresIn = userData.expiresIn * 1000; // Convert to milliseconds
    const now = Date.now();
    
    // Refresh if token expires within the next 5 minutes (300,000 ms)
    const timeUntilExpiry = (loginTime + expiresIn) - now;
    const shouldRefresh = timeUntilExpiry < 300000 && timeUntilExpiry > 0;
    
    return shouldRefresh;
  }

  private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler, userData: any): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      
      console.log('🔄 Starting token refresh process');

      // Always get the latest refresh token from localStorage in case it was just updated
      const latestUserData = localStorage.getItem("user");
      if (latestUserData) {
        const latestUser = JSON.parse(latestUserData);
        if (latestUser.refreshToken) {
          userData.refreshToken = latestUser.refreshToken;
        }
      }

      return this.refreshToken(userData.refreshToken).pipe(
        switchMap((newTokenData: any) => {
          
          if (newTokenData && newTokenData.accessToken) {
            console.log(`✅ Token refreshed (expires: ${newTokenData.expiresIn}s, refresh: ${newTokenData.refreshToken ? newTokenData.refreshToken.substring(0, 10) + '...' : 'MISSING!'})`);
            
            // Update stored user data with new tokens
            const updatedUserData = {
              ...userData,
              accessToken: newTokenData.accessToken,
              refreshToken: newTokenData.refreshToken || userData.refreshToken, // Fallback if not provided
              loginTime: Date.now(),
              expiresIn: newTokenData.expiresIn
            };
            
            if (!newTokenData.refreshToken) {
              console.warn('⚠️ No refresh token in response, keeping existing');
            } else {
              console.log(`💾 Storing new refresh token: ${updatedUserData.refreshToken.substring(0, 10)}...`);
            }
            
            localStorage.setItem('user', JSON.stringify(updatedUserData));
            localStorage.setItem('loginTime', Date.now().toString());
            
            // Notify all waiting requests with the new token
            this.refreshTokenSubject.next(newTokenData.accessToken);
            
            // Reset the isRefreshing flag AFTER notifying waiting requests
            this.isRefreshing = false;
            
            // Retry the original request with the new token
            const newRequest = request.clone({
              setHeaders: { Authorization: `Bearer ${newTokenData.accessToken}` }
            });
            
            // Show success notification in development
            if (!environment.production && typeof window !== 'undefined' && window.Notification) {
              if (Notification.permission === 'granted') {
                new Notification('✅ Token Refreshed', {
                  body: 'Your session has been extended for another 15 minutes',
                  icon: '/assets/icon/favicon.png'
                });
              }
            }
            return next.handle(newRequest);
          } else {
            // Refresh failed, redirect to login
            this.isRefreshing = false;
            this.refreshTokenSubject.next(null);
            this.handleRefreshFailure();
            return throwError('Token refresh failed');
          }
        }),
        catchError((error) => {
          console.error('❌ Token refresh failed:', error);
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null); // Reset the subject
          this.handleRefreshFailure();
          return throwError(error);
        })
      );
    } else {
      // Token refresh is already in progress, wait for it to complete
      console.log('⏳ Token refresh in progress, queuing request');
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          console.log('▶️ Resuming queued request with new token');
          const newRequest = request.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
          });
          return next.handle(newRequest);
        }),
        catchError((error) => {
          // If waiting for refresh fails, try to get the latest token from storage
          const latestUserData = localStorage.getItem("user");
          if (latestUserData) {
            const latestUser = JSON.parse(latestUserData);
            if (latestUser.accessToken) {
              console.log('🔄 Using latest token from storage');
              const newRequest = request.clone({
                setHeaders: { Authorization: `Bearer ${latestUser.accessToken}` }
              });
              return next.handle(newRequest);
            }
          }
          return throwError(error);
        })
      );
    }
  }

  private refreshToken(refreshToken: string): Observable<any> {
    const apiUrl = environment.apiUrl || (environment as any).config?.hostName || 'http://localhost:3000';
    
    // Always get the latest refresh token from localStorage in case it was updated
    const latestUserData = localStorage.getItem("user");
    if (latestUserData) {
      const latestUser = JSON.parse(latestUserData);
      if (latestUser.refreshToken) {
        const oldToken = refreshToken;
        refreshToken = latestUser.refreshToken;
        if (oldToken !== refreshToken) {
          // console.log('🔄 Using updated refresh token');
        }
      }
    }
    
    // console.log(`🔑 Sending refresh token: ${refreshToken.substring(0, 10)}...`);
    
    return new Observable(observer => {
      this.refreshTokenWithRetry(apiUrl, refreshToken, 0).then(data => {
        // console.log('📥 Refresh response data:', {
        //   hasAccessToken: !!data.accessToken,
        //   hasRefreshToken: !!data.refreshToken,
        //   refreshTokenPreview: data.refreshToken ? data.refreshToken.substring(0, 10) + '...' : 'MISSING'
        // });
        observer.next(data);
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  private async refreshTokenWithRetry(apiUrl: string, refreshToken: string, attemptNumber: number): Promise<any> {
    const maxRetries = 3;
    const baseDelay = 1000; // Start with 1 second
    
    try {
      const response = await fetch(`${apiUrl}/api/token/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        return await response.json();
      } else {
        // If it's an auth error (401, 403), don't retry
        if (response.status === 401 || response.status === 403) {
          // console.error(`❌ Auth error (${response.status}): Token refresh denied`);
          throw new Error(`Auth error: ${response.status}`);
        }
        
        // For other errors, consider retry
        if (attemptNumber < maxRetries - 1) {
          // console.warn(`⚠️ Refresh failed with status ${response.status}, retrying (attempt ${attemptNumber + 1}/${maxRetries})...`);
          throw new Error(`HTTP error: ${response.status}`);
        } else {
          // console.error(`❌ Refresh failed with status: ${response.status} after ${maxRetries} attempts`);
          throw new Error(`Refresh failed after ${maxRetries} attempts`);
        }
      }
    } catch (error: any) {
      // Network error or HTTP error that should be retried
      if (attemptNumber < maxRetries - 1) {
        // Calculate exponential backoff with jitter
        const delay = baseDelay * Math.pow(2, attemptNumber) + Math.random() * 1000;
        
        // console.warn(`🔄 Retry ${attemptNumber + 1}/${maxRetries} after ${Math.round(delay)}ms...`);
        
        // Show notification in development
        if (!environment.production && typeof window !== 'undefined' && window.Notification) {
          if (Notification.permission === 'granted') {
            new Notification('🔄 Token Refresh Retry', {
              body: `Retrying in ${Math.round(delay / 1000)}s (attempt ${attemptNumber + 1}/${maxRetries})`,
              icon: '/assets/icon/favicon.png'
            });
          }
        }
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.refreshTokenWithRetry(apiUrl, refreshToken, attemptNumber + 1);
      } else {
        // Max retries reached or auth error
        throw error;
      }
    }
  }

  private handleRefreshFailure() {
    console.log('🔒 Refresh failed → login');
    
    // Store the intended URL for redirecting after login
    const returnUrl = this.router.url;
    if (returnUrl && returnUrl !== '/' && returnUrl !== '/login') {
      localStorage.setItem('returnUrl', returnUrl);
    }
    
    // Clear all auth data
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("loginTime");
    localStorage.removeItem("refreshToken");
    
    // Navigate to login
    this.navCtrl.navigateRoot("/login");
  }

}
